import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ApprovalHierarchy from './ApprovalHierarchy';
import AuditTrail from './AuditTrail';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { FaceRegistrationPanel } from '../components/FaceAttendance';
import { ProfileCorrections } from '../components/ProfileCorrections';
import { MyEmployeeProfile } from '../components/MyEmployeeProfile';
import { Badge } from '../components/ui/badge';
import {
  User, Lock, Bell, Palette, Shield, Database, Save, Upload,
  GitBranch, ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE as API_URL } from '../lib/apiBase';
import { safeJson } from '../lib/safeJson';
export default function Settings() {
  const { user, refreshUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    department: user?.department || ''
  });
  const [notifications, setNotifications] = useState({
    email_alerts: true,
    leave_notifications: true,
    payroll_alerts: true,
    attendance_reminders: true
  });

  // Keep profile form in sync when user updates externally (e.g. admin edits)
  useEffect(() => {
    setProfileForm(prev => ({
      ...prev,
      name: user?.name || prev.name,
      email: user?.email || prev.email,
      department: user?.department || prev.department
    }));
  }, [user?.name, user?.email, user?.department]);

  useEffect(() => {
    const fetchMyPhoto = async () => {
      try {
        const token = localStorage.getItem('hrms-token');
        // First check if user has a user-level photo
        if (user?.user_id) {
          const userPhotoRes = await fetch(`${API_URL}/api/users/${user.user_id}/photo`);
          if (userPhotoRes.ok) {
            setPhotoUrl(`${API_URL}/api/users/${user.user_id}/photo?t=${Date.now()}`);
          }
        }
        // Try employee profile to seed phone + employee_id
        const meRes = await fetch(`${API_URL}/api/me/employee-profile`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' });
        if (meRes.ok) {
          const me = await meRes.json();
          if (me) {
            setEmployeeId(me.employee_id);
            setProfileForm(prev => ({ ...prev, phone: me.phone || prev.phone }));
            if (me.profile_picture && !photoUrl) setPhotoUrl(`${API_URL}${me.profile_picture}?t=${Date.now()}`);
          }
        }
      } catch {}
    };
    fetchMyPhoto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, user?.user_id]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPEG, PNG, WebP images allowed');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File must be under 2MB');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      const token = localStorage.getItem('hrms-token');
      // Use user-level photo upload (works for all roles)
      const res = await fetch(`${API_URL}/api/users/me/photo`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData, credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setPhotoUrl(`${API_URL}${data.photo_url}?t=${Date.now()}`);
        toast.success('Photo uploaded successfully');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to upload');
      }
    } catch { toast.error('Upload failed'); }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const res = await fetch(`${API_URL}/api/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: profileForm.name,
          phone: profileForm.phone,
          department: profileForm.department
        }),
        credentials: 'include'
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to update profile');
      }
      // Refresh global user state so updated name/department reflect across the app
      if (refreshUser) await refreshUser();
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
          Settings
        </h1>
        <p className="text-slate-500 dark:text-slate-400">
          Manage your account and system preferences
        </p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <div className="w-full max-w-full overflow-x-auto -mx-3 px-3 md:mx-0 md:px-0">
        <TabsList className="w-max">
          <TabsTrigger value="profile" data-testid="tab-profile">
            <User className="w-4 h-4 mr-2" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="appearance" data-testid="tab-appearance">
            <Palette className="w-4 h-4 mr-2" />
            Appearance
          </TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications">
            <Bell className="w-4 h-4 mr-2" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">
            <Shield className="w-4 h-4 mr-2" />
            Security
          </TabsTrigger>
          <TabsTrigger value="approval" data-testid="tab-approval">
            <GitBranch className="w-4 h-4 mr-2" />
            Approval Flow
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">
            <ClipboardList className="w-4 h-4 mr-2" />
            Audit Trail
          </TabsTrigger>
        </TabsList>
        </div>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Profile Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center text-center">
                  <Avatar className="w-24 h-24 mb-4">
                    <AvatarImage src={photoUrl} />
                    <AvatarFallback className="bg-[#DC2626] text-white text-2xl">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white">
                    {user?.name || 'User'}
                  </h3>
                  <p className="text-slate-500">{user?.email}</p>
                  <Badge className="mt-2 capitalize">{user?.role?.replace('_', ' ') || 'Employee'}</Badge>
                  <label htmlFor="photo-upload" className="cursor-pointer">
                    <Button variant="outline" className="mt-4" asChild data-testid="change-avatar-btn">
                      <span><Upload className="w-4 h-4 mr-2" />Upload Photo</span>
                    </Button>
                    <input id="photo-upload" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handlePhotoUpload} />
                  </label>
                  <p className="text-xs text-slate-400 mt-1">JPG, PNG, WebP (Max 2MB)</p>
                </div>
              </CardContent>
            </Card>

            {/* Profile Form */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>Update your personal details</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleProfileUpdate} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name</Label>
                      <Input
                        id="name"
                        value={profileForm.name}
                        onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                        data-testid="profile-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={profileForm.email}
                        onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                        disabled
                        data-testid="profile-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        value={profileForm.phone}
                        onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                        placeholder="+91 XXXXX XXXXX"
                        data-testid="profile-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="department">Department <span className="text-[10px] text-slate-400">(change via Request Correction below)</span></Label>
                      <Input
                        id="department"
                        value={profileForm.department}
                        disabled
                        data-testid="profile-department-input"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button type="submit" className="btn-primary" disabled={loading} data-testid="save-profile-btn">
                      <Save className="w-4 h-4 mr-2" />
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Full employee record — read-only official data + self-editable personal fields */}
          <div className="mt-6">
            <MyEmployeeProfile />
          </div>

          {/* Profile correction request panel (employee self-service) */}
          <div className="mt-6">
            <ProfileCorrections />
          </div>
        </TabsContent>

        {/* Appearance Tab */}
        <TabsContent value="appearance">
          <Card>
            <CardHeader>
              <CardTitle>Appearance</CardTitle>
              <CardDescription>Customize the look and feel of the application</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Theme</p>
                  <p className="text-sm text-slate-500">Select your preferred theme</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant={theme === 'light' ? 'default' : 'outline'}
                    onClick={() => setTheme('light')}
                    data-testid="theme-light-btn"
                  >
                    Light
                  </Button>
                  <Button
                    variant={theme === 'dark' ? 'default' : 'outline'}
                    onClick={() => setTheme('dark')}
                    data-testid="theme-dark-btn"
                  >
                    Dark
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
                      setTheme(systemTheme);
                    }}
                    data-testid="theme-system-btn"
                  >
                    System
                  </Button>
                </div>
              </div>

              <div className="border-t pt-6">
                <p className="font-medium mb-4">Preview</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-white border rounded-sm">
                    <p className="font-medium text-slate-900">Light Mode</p>
                    <p className="text-sm text-slate-500">Clean and professional</p>
                  </div>
                  <div className="p-4 bg-slate-900 border border-slate-700 rounded-sm">
                    <p className="font-medium text-white">Dark Mode</p>
                    <p className="text-sm text-slate-400">Easy on the eyes</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
              <CardDescription>Manage how you receive notifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Email Alerts</p>
                  <p className="text-sm text-slate-500">Receive important alerts via email</p>
                </div>
                <Switch
                  checked={notifications.email_alerts}
                  onCheckedChange={(checked) => setNotifications({ ...notifications, email_alerts: checked })}
                  data-testid="email-alerts-switch"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Leave Notifications</p>
                  <p className="text-sm text-slate-500">Get notified about leave requests and approvals</p>
                </div>
                <Switch
                  checked={notifications.leave_notifications}
                  onCheckedChange={(checked) => setNotifications({ ...notifications, leave_notifications: checked })}
                  data-testid="leave-notifications-switch"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Payroll Alerts</p>
                  <p className="text-sm text-slate-500">Notifications about salary processing</p>
                </div>
                <Switch
                  checked={notifications.payroll_alerts}
                  onCheckedChange={(checked) => setNotifications({ ...notifications, payroll_alerts: checked })}
                  data-testid="payroll-alerts-switch"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Attendance Reminders</p>
                  <p className="text-sm text-slate-500">Daily reminders to mark attendance</p>
                </div>
                <Switch
                  checked={notifications.attendance_reminders}
                  onCheckedChange={(checked) => setNotifications({ ...notifications, attendance_reminders: checked })}
                  data-testid="attendance-reminders-switch"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Change Password</CardTitle>
                <CardDescription>Update your password regularly for security</CardDescription>
              </CardHeader>
              <CardContent>
                <form className="space-y-4" onSubmit={async (e) => {
                  e.preventDefault();
                  const curr = e.target.elements['current-password'].value;
                  const newP = e.target.elements['new-password'].value;
                  const conf = e.target.elements['confirm-password'].value;
                  if (!curr || !newP) { toast.error('Please fill all fields'); return; }
                  if (newP.length < 6) { toast.error('New password must be at least 6 characters'); return; }
                  if (newP !== conf) { toast.error('Passwords do not match'); return; }
                  if (newP === curr) { toast.error('New password must be different from current password'); return; }
                  const form = e.target;
                  try {
                    const token = localStorage.getItem('hrms-token');
                    const res = await fetch(`${API_URL}/api/auth/change-password`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
                      body: JSON.stringify({ current_password: curr, new_password: newP }),
                      credentials: 'include',
                    });
                    const data = await safeJson(res);
                    if (!res.ok) {
                      let detail = data.detail;
                      if (Array.isArray(detail)) detail = detail.map(d => (d && d.msg) ? d.msg : JSON.stringify(d)).join(' ');
                      throw new Error(detail || data.message || `Failed (HTTP ${res.status})`);
                    }
                    toast.success('Password changed successfully!');
                    form.reset();
                  } catch (err) { toast.error(err.message); }
                }}>
                  <div className="space-y-2">
                    <Label htmlFor="current-password">Current Password</Label>
                    <Input
                      id="current-password"
                      type="password"
                      placeholder="Enter current password"
                      data-testid="current-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      placeholder="Min 6 characters"
                      data-testid="new-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm New Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      placeholder="Re-enter new password"
                      data-testid="confirm-password-input"
                    />
                  </div>
                  <Button type="submit" className="btn-primary" data-testid="update-password-btn">
                    <Lock className="w-4 h-4 mr-2" />
                    Update Password
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Session Management</CardTitle>
                <CardDescription>Manage your active sessions</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Current Session</p>
                      <p className="text-sm text-slate-500">This device • Active now</p>
                    </div>
                    <Badge className="badge-success">Active</Badge>
                  </div>
                </div>
                <Button variant="outline" className="w-full text-red-600" data-testid="logout-all-btn">
                  Logout from all devices
                </Button>
              </CardContent>
            </Card>

            {/* Face Registration */}
            {employeeId && (
              <div className="lg:col-span-2">
                <FaceRegistrationPanel
                  employeeId={employeeId}
                  isHR={['super_admin', 'admin', 'hr'].includes(user?.role)}
                />
              </div>
            )}

            <Card className="lg:col-span-2">
              <CardHeader><CardTitle>Account Information</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded">
                    <p className="text-sm text-slate-500">User ID</p>
                    <code className="font-mono text-sm">{user?.user_id || 'N/A'}</code>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded">
                    <p className="text-sm text-slate-500">Role</p>
                    <p className="font-medium capitalize">{user?.role?.replace('_', ' ') || 'Employee'}</p>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded">
                    <p className="text-sm text-slate-500">Account Status</p>
                    <Badge className="badge-success">Active</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Approval Flow Tab (moved from sidebar) */}
        <TabsContent value="approval" className="space-y-6">
          <ApprovalHierarchy embedded />
        </TabsContent>

        {/* Audit Trail Tab (moved from sidebar) */}
        <TabsContent value="audit" className="space-y-6">
          <AuditTrail embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
}
